import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { FaCalculator, FaChartLine, FaCogs } from 'react-icons/fa';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${(props) => props.theme.secondaryText};
  text-align: center;
  margin-bottom: 2.5rem;
  line-height: 1.5;
`;

const SectionLabel = styled.h2`
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(props) => props.theme.secondaryText};
  margin-bottom: 1rem;
  margin-top: 2rem;
  &:first-of-type {
    margin-top: 0;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;

const Card = styled(Link)`
  display: block;
  background: ${(props) => props.theme.cardBg};
  border-radius: 0.5rem;
  padding: 1.25rem;
  box-shadow: ${(props) => props.theme.shadow};
  border: 1px solid ${(props) => props.theme.border};
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: ${(props) => props.theme.primary};
    box-shadow: ${(props) => props.theme.shadowHover};
  }
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardDescription = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.secondaryText};
  line-height: 1.45;
`;

const ResourcesLandingPage = () => {
  const { theme } = useTheme();
  usePageTitle('Betting Calculators & Guides');

  return (
    <Container>
      <PageMeta description="Access Slick Picks resources including betting calculators and strategy guides to help you understand odds, parlays, and data-driven MLB betting." />
      <Title theme={theme}>Resources</Title>
      <Subtitle theme={theme}>
        Calculators and guides to help you bet smarter and get the most out of SlickPicks.
      </Subtitle>

      <SectionLabel theme={theme}>Calculators</SectionLabel>
      <CardGrid>
        <Card theme={theme} to="/resources/odds-calculator">
          <CardTitle theme={theme}>
            <FaCalculator size={18} aria-hidden />
            Betting Odds Calculator
          </CardTitle>
          <CardDescription theme={theme}>
            Enter your stake and odds in American, Decimal, or Fractional format to see your potential payout and implied probability.
          </CardDescription>
        </Card>
        <Card theme={theme} to="/resources/parlay-calculator">
          <CardTitle theme={theme}>
            <FaCalculator size={18} aria-hidden />
            Parlay Calculator
          </CardTitle>
          <CardDescription theme={theme}>
            Add multiple legs and your bet amount to calculate combined parlay odds and potential winnings.
          </CardDescription>
        </Card>
      </CardGrid>

      <SectionLabel theme={theme}>Guides</SectionLabel>
      <CardGrid>
        <Card theme={theme} to="/how-it-works">
          <CardTitle theme={theme}>
            <FaCogs size={18} aria-hidden />
            How It Works
          </CardTitle>
          <CardDescription theme={theme}>
            See how proprietary features, XGBoost models, SHAP insights, live odds, and LLM curation produce today&apos;s picks.
          </CardDescription>
        </Card>
        <Card theme={theme} to="/resources/betting-strategies">
          <CardTitle theme={theme}>
            <FaChartLine size={18} aria-hidden />
            Betting Strategies
          </CardTitle>
          <CardDescription theme={theme}>
            Learn approaches to bankroll management, line shopping, and using data to improve your edge.
          </CardDescription>
        </Card>
      </CardGrid>
    </Container>
  );
};

export default ResourcesLandingPage;
